import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { TrendingUp, Plus, Weight, TruckIcon, Shovel, Construction, Wrench, ClipboardList, HardHat, Leaf, Info, Users, Database, Lock, Brain, Layers, Trash2, PackageOpen } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import Header from '../components/Header';
import MaterialCard from '../components/MaterialCard';
import SummaryCard from '../components/SummaryCard';
import BulkMaterialCard from '../components/BulkMaterialCard';
import BulkSummaryCard from '../components/BulkSummaryCard';
import ExcavationCalculator from '../components/ExcavationCalculator';
import TruckingCalculator from '../components/TruckingCalculator';
import PipingCalculator from '../components/PipingCalculator';
import TanksCalculator from '../components/TanksCalculator';
import BuildingCalculator from '../components/BuildingCalculator';
import BackfillCalculator from '../components/BackfillCalculator';
import MobilizationCalculator from '../components/MobilizationCalculator';
import CustomVolumeCalculator from '../components/CustomVolumeCalculator';
import QuickCalculators from '../components/QuickCalculators';
import EngineeringTools from '../components/EngineeringTools';
import AdvancedCalculators from '../components/AdvancedCalculators';
import EstimateSpreadsheet from '../components/EstimateSpreadsheet';
import CustomMaterialsManager from '../components/CustomMaterialsManager';
import AITroubleshooter from '../components/AITroubleshooter';
import CivilCalculator from '../components/CivilCalculator';
import EnvironmentalCalculator from '../components/EnvironmentalCalculator';
import RSMeansDatabase from '../components/RSMeansDatabase';
import ContractorDatabase from '../components/ContractorDatabase';
import DocumentAnalyzer from '../components/DocumentAnalyzer';
import LaborEquipmentInput from '../components/LaborEquipmentInput';
import { defaultLaborTypes, defaultEquipmentTypes } from '../components/LaborEquipmentRates';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { materialDatabase, bulkMaterialDatabase } from '../data/materials';
import { excavationDatabase } from '../data/excavation';
import { toast } from 'sonner';
import { 
  saveProjectToStorage, 
  getLastSaveTime,
  getCurrentProjectData 
} from '../utils/saveUtils';
import { initializeAppData } from '../utils/versionManager';
import { useAuth } from '../context/AuthContext';

// Tab info tooltips
const TAB_TOOLTIPS = {
  mobilization: "Mobilization & Demobilization - project setup, equipment transport, labor",
  demo: "Calculate demolition material weights - concrete, asphalt, roofing, and more",
  import: "Calculate import materials - gravel, sand, soil with trucking & material costs",
  backfill: "Calculate backfill materials with compaction factors, trucking & material costs",
  excavation: "Calculate excavation volumes with swell factors for different soil types",
  civil: "Civil construction - paving, drainage, utilities, curb & gutter",
  environmental: "Environmental compliance - erosion control, stormwater, remediation",
  trucking: "Calculate trucking costs, load counts, and disposal fees",
  tools: "Pro tools including quick calculators, engineering tools, and more",
  estimate: "Generate professional project estimates with labor, equipment, and markup",
  rsmeans: "Regional construction cost database with cost factors",
  contractors: "Manage your contractor database with contact info and certifications"
};

// Filter materials for custom calculators
const customImportMaterialsList = bulkMaterialDatabase.filter(m => 
  m.category === 'Sand & Gravel' || 
  m.category === 'Gravel & Stone' ||
  m.category === 'Soil & Dirt' ||
  m.category === 'Base Materials'
);

// Convert excavation materials to use tonsPerCubicYard property for custom calculator
const customExcavationMaterialsList = excavationDatabase.map(m => ({
  ...m,
  tonsPerCubicYard: m.tonsPerBCY
}));

const CalculatorPage = ({ visitCount }) => {
  // Area-based materials state - initialized from localStorage
  const [materials, setMaterials] = useState(() => {
    const saved = localStorage.getItem('calculatorMaterials');
    return saved ? JSON.parse(saved) : [];
  });
  const [selectedMaterial, setSelectedMaterial] = useState('');
  const [squareFeet, setSquareFeet] = useState('');
  const [demoCostPerTon, setDemoCostPerTon] = useState('');
  const [customWeight, setCustomWeight] = useState(''); // Custom weight override
  
  // Custom material databases (user-editable)
  const [customDemoMaterials, setCustomDemoMaterials] = useState(() => {
    const saved = localStorage.getItem('customDemoMaterials');
    return saved ? JSON.parse(saved) : [];
  });
  
  const [customBulkMaterials, setCustomBulkMaterials] = useState(() => {
    const saved = localStorage.getItem('customBulkMaterials');
    return saved ? JSON.parse(saved) : [];
  });
  
  const [customExcavationDB, setCustomExcavationDB] = useState(() => {
    const saved = localStorage.getItem('customExcavationDB');
    return saved ? JSON.parse(saved) : [];
  });
  
  // Volume-based materials state - initialized from localStorage
  const [bulkMaterials, setBulkMaterials] = useState(() => {
    const saved = localStorage.getItem('bulkCalculatorMaterials');
    return saved ? JSON.parse(saved) : [];
  });
  const [selectedBulkMaterial, setSelectedBulkMaterial] = useState('');
  const [cubicYards, setCubicYards] = useState('');
  const [bulkCostPerTon, setBulkCostPerTon] = useState(''); // Material cost
  const [bulkTruckingCostPerTon, setBulkTruckingCostPerTon] = useState(''); // Trucking cost
  const [customBulkWeight, setCustomBulkWeight] = useState(''); // Custom weight override for bulk
  
  // Excavation materials state
  const [excavationMaterials, setExcavationMaterials] = useState(() => {
    const saved = localStorage.getItem('excavationMaterials');
    return saved ? JSON.parse(saved) : [];
  });
  
  const [pipingMaterials, setPipingMaterials] = useState(() => {
    const saved = localStorage.getItem('pipingMaterials');
    return saved ? JSON.parse(saved) : [];
  });
  
  const [tankMaterials, setTankMaterials] = useState(() => {
    const saved = localStorage.getItem('tankMaterials');
    return saved ? JSON.parse(saved) : [];
  });
  
  const [buildings, setBuildings] = useState(() => {
    const saved = localStorage.getItem('buildingMaterials');
    return saved ? JSON.parse(saved) : [];
  });
  
  const [customImportVolumes, setCustomImportVolumes] = useState(() => {
    const saved = localStorage.getItem('customImportVolumes');
    return saved ? JSON.parse(saved) : [];
  });
  
  const [customExcavationVolumes, setCustomExcavationVolumes] = useState(() => {
    const saved = localStorage.getItem('customExcavationVolumes');
    return saved ? JSON.parse(saved) : [];
  });
  
  // Trucking calculations state
  const [truckingCalculations, setTruckingCalculations] = useState([]);

  // Civil construction items state
  const [civilItems, setCivilItems] = useState(() => {
    const saved = localStorage.getItem('civilItems');
    return saved ? JSON.parse(saved) : [];
  });

  // Environmental items state
  const [environmentalItems, setEnvironmentalItems] = useState(() => {
    const saved = localStorage.getItem('environmentalItems');
    return saved ? JSON.parse(saved) : [];
  });

  // Backfill items state
  const [backfillItems, setBackfillItems] = useState(() => {
    const saved = localStorage.getItem('backfillItems');
    return saved ? JSON.parse(saved) : [];
  });

  // Labor & Equipment state for each section
  const [demoLabor, setDemoLabor] = useState(() => {
    const saved = localStorage.getItem('demoLabor');
    return saved ? JSON.parse(saved) : [];
  });
  const [demoEquipment, setDemoEquipment] = useState(() => {
    const saved = localStorage.getItem('demoEquipment');
    return saved ? JSON.parse(saved) : [];
  });
  const [excavationLabor, setExcavationLabor] = useState(() => {
    const saved = localStorage.getItem('excavationLabor');
    return saved ? JSON.parse(saved) : [];
  });
  const [excavationEquipment, setExcavationEquipment] = useState(() => {
    const saved = localStorage.getItem('excavationEquipment');
    return saved ? JSON.parse(saved) : [];
  });
  const [backfillLabor, setBackfillLabor] = useState(() => {
    const saved = localStorage.getItem('backfillLabor');
    return saved ? JSON.parse(saved) : [];
  });
  const [backfillEquipment, setBackfillEquipment] = useState(() => {
    const saved = localStorage.getItem('backfillEquipment');
    return saved ? JSON.parse(saved) : [];
  });
  const [importLabor, setImportLabor] = useState(() => {
    const saved = localStorage.getItem('importLabor');
    return saved ? JSON.parse(saved) : [];
  });
  const [importEquipment, setImportEquipment] = useState(() => {
    const saved = localStorage.getItem('importEquipment');
    return saved ? JSON.parse(saved) : [];
  });
  const [civilLabor, setCivilLabor] = useState(() => {
    const saved = localStorage.getItem('civilLabor');
    return saved ? JSON.parse(saved) : [];
  });
  const [civilEquipment, setCivilEquipment] = useState(() => {
    const saved = localStorage.getItem('civilEquipment');
    return saved ? JSON.parse(saved) : [];
  });
  const [environmentalLabor, setEnvironmentalLabor] = useState(() => {
    const saved = localStorage.getItem('environmentalLabor');
    return saved ? JSON.parse(saved) : [];
  });
  const [environmentalEquipment, setEnvironmentalEquipment] = useState(() => {
    const saved = localStorage.getItem('environmentalEquipment');
    return saved ? JSON.parse(saved) : [];
  });

  // Task Durations for each tab
  const [demoTaskDuration, setDemoTaskDuration] = useState(() => {
    const saved = localStorage.getItem('demoTaskDuration');
    return saved ? JSON.parse(saved) : { value: '', unit: 'days' };
  });
  const [excavationTaskDuration, setExcavationTaskDuration] = useState(() => {
    const saved = localStorage.getItem('excavationTaskDuration');
    return saved ? JSON.parse(saved) : { value: '', unit: 'days' };
  });
  const [backfillTaskDuration, setBackfillTaskDuration] = useState(() => {
    const saved = localStorage.getItem('backfillTaskDuration');
    return saved ? JSON.parse(saved) : { value: '', unit: 'days' };
  });
  const [importTaskDuration, setImportTaskDuration] = useState(() => {
    const saved = localStorage.getItem('importTaskDuration');
    return saved ? JSON.parse(saved) : { value: '', unit: 'days' };
  });
  const [civilTaskDuration, setCivilTaskDuration] = useState(() => {
    const saved = localStorage.getItem('civilTaskDuration');
    return saved ? JSON.parse(saved) : { value: '', unit: 'days' };
  });
  const [environmentalTaskDuration, setEnvironmentalTaskDuration] = useState(() => {
    const saved = localStorage.getItem('environmentalTaskDuration');
    return saved ? JSON.parse(saved) : { value: '', unit: 'days' };
  });

  // Mobilization & Demobilization
  const [mobilization, setMobilization] = useState(() => {
    const saved = localStorage.getItem('mobilization');
    return saved ? JSON.parse(saved) : { enabled: false, cost: 0, method: 'fixed' };
  });

  // Project type selection
  const [projectType, setProjectType] = useState(() => {
    const saved = localStorage.getItem('projectType');
    return saved || 'all';
  });

  // Save state
  const [lastSaveTime, setLastSaveTime] = useState(() => getLastSaveTime());

  // Project management state
  const [projectInfo, setProjectInfo] = useState(() => {
    const saved = localStorage.getItem('projectInfo');
    return saved ? JSON.parse(saved) : {
      name: '',
      number: '',
      date: new Date().toISOString().split('T')[0],
      location: '',
      clientName: '',
      description: ''
    };
  });

  const [companyInfo, setCompanyInfo] = useState(() => {
    const saved = localStorage.getItem('companyInfo');
    return saved ? JSON.parse(saved) : {
      name: '',
      address: '',
      phone: '',
      email: '',
      website: '',
      license: ''
    };
  });

  // Mobilization/Demobilization data state
  const [mobilizationData, setMobilizationData] = useState(() => {
    const saved = localStorage.getItem('mobilizationData');
    return saved ? JSON.parse(saved) : {
      mobilization: {
        duration: { value: '', unit: 'days' },
        equipment: [],
        labor: [],
        other: []
      },
      demobilization: {
        duration: { value: '', unit: 'days' },
        equipment: [],
        labor: [],
        other: []
      }
    };
  });

  // Save mobilization data to localStorage
  useEffect(() => {
    localStorage.setItem('mobilizationData', JSON.stringify(mobilizationData));
  }, [mobilizationData]);

  // Clear mobilization tab
  const clearMobilizationTab = () => {
    setMobilizationData({
      mobilization: {
        duration: { value: '', unit: 'days' },
        equipment: [],
        labor: [],
        other: []
      },
      demobilization: {
        duration: { value: '', unit: 'days' },
        equipment: [],
        labor: [],
        other: []
      }
    });
    localStorage.removeItem('mobilizationData');
    toast.success('Mobilization tab cleared');
  };

  // Quick save handler
  const handleQuickSave = () => {
    const data = {
      projectInfo,
      companyInfo,
      calculators: {
        areaMaterials: materials,
        bulkMaterials,
        excavationMaterials,
        pipingMaterials,
        tankMaterials,
        buildings,
        truckingCalculations,
        customImportVolumes,
        customExcavationVolumes
      }
    };
    const name = projectInfo.name || `Project_${Date.now()}`;
    const result = saveProjectToStorage(data, name);
    
    if (result.success) {
      toast.success('Project saved!');
      setLastSaveTime(result.savedAt);
    } else {
      toast.error('Failed to save project');
    }
  };

  const [projectNotes, setProjectNotes] = useState(() => {
    const saved = localStorage.getItem('projectNotes');
    return saved ? JSON.parse(saved) : [];
  });

  // Costing calculator state
  const [costingData, setCostingData] = useState(null);

  // Labor and Equipment types
  const [laborTypes, setLaborTypes] = useState(() => {
    const saved = localStorage.getItem('laborTypes');
    return saved ? JSON.parse(saved) : defaultLaborTypes;
  });

  const [equipmentTypes, setEquipmentTypes] = useState(() => {
    const saved = localStorage.getItem('equipmentTypes');
    return saved ? JSON.parse(saved) : defaultEquipmentTypes;
  });

  // Initialize app version management
  useEffect(() => {
    const versionInfo = initializeAppData();
    if (versionInfo.wasUpdated) {
      toast.success(`App updated to version 2.0! Check out the new AI features.`);
    }
  }, []);

  // Save labor and equipment to localStorage
  useEffect(() => {
    localStorage.setItem('laborTypes', JSON.stringify(laborTypes));
  }, [laborTypes]);

  useEffect(() => {
    localStorage.setItem('equipmentTypes', JSON.stringify(equipmentTypes));
  }, [equipmentTypes]);

  // Save custom material databases to localStorage
  useEffect(() => {
    localStorage.setItem('customDemoMaterials', JSON.stringify(customDemoMaterials));
  }, [customDemoMaterials]);

  useEffect(() => {
    localStorage.setItem('customBulkMaterials', JSON.stringify(customBulkMaterials));
  }, [customBulkMaterials]);

  useEffect(() => {
    localStorage.setItem('customExcavationDB', JSON.stringify(customExcavationDB));
  }, [customExcavationDB]);

  // Save project info to localStorage
  useEffect(() => {
    localStorage.setItem('projectInfo', JSON.stringify(projectInfo));
  }, [projectInfo]);

  useEffect(() => {
    localStorage.setItem('companyInfo', JSON.stringify(companyInfo));
  }, [companyInfo]);

  useEffect(() => {
    localStorage.setItem('projectNotes', JSON.stringify(projectNotes));
  }, [projectNotes]);

  // Save materials to localStorage whenever they change
  useEffect(() => {
    if (materials.length > 0) {
      localStorage.setItem('calculatorMaterials', JSON.stringify(materials));
    } else {
      localStorage.removeItem('calculatorMaterials');
    }
  }, [materials]);

  useEffect(() => {
    if (bulkMaterials.length > 0) {
      localStorage.setItem('bulkCalculatorMaterials', JSON.stringify(bulkMaterials));
    } else {
      localStorage.removeItem('bulkCalculatorMaterials');
    }
  }, [bulkMaterials]);

  useEffect(() => {
    if (excavationMaterials.length > 0) {
      localStorage.setItem('excavationMaterials', JSON.stringify(excavationMaterials));
    } else {
      localStorage.removeItem('excavationMaterials');
    }
  }, [excavationMaterials]);

  // Save piping materials to localStorage
  useEffect(() => {
    localStorage.setItem('pipingMaterials', JSON.stringify(pipingMaterials));
  }, [pipingMaterials]);
  
  // Save tank materials to localStorage
  useEffect(() => {
    localStorage.setItem('tankMaterials', JSON.stringify(tankMaterials));
  }, [tankMaterials]);
  
  // Save building materials to localStorage
  useEffect(() => {
    localStorage.setItem('buildingMaterials', JSON.stringify(buildings));
  }, [buildings]);
  
  // Save custom import volumes to localStorage
  useEffect(() => {
    localStorage.setItem('customImportVolumes', JSON.stringify(customImportVolumes));
  }, [customImportVolumes]);
  
  // Save custom excavation volumes to localStorage
  useEffect(() => {
    localStorage.setItem('customExcavationVolumes', JSON.stringify(customExcavationVolumes));
  }, [customExcavationVolumes]);

  // Save civil items to localStorage
  useEffect(() => {
    localStorage.setItem('civilItems', JSON.stringify(civilItems));
  }, [civilItems]);

  // Save environmental items to localStorage
  useEffect(() => {
    localStorage.setItem('environmentalItems', JSON.stringify(environmentalItems));
  }, [environmentalItems]);

  // Save backfill items to localStorage
  useEffect(() => {
    localStorage.setItem('backfillItems', JSON.stringify(backfillItems));
  }, [backfillItems]);

  // Save project type to localStorage
  useEffect(() => {
    localStorage.setItem('projectType', projectType);
  }, [projectType]);

  // Save labor & equipment for each section
  useEffect(() => {
    localStorage.setItem('demoLabor', JSON.stringify(demoLabor));
  }, [demoLabor]);
  useEffect(() => {
    localStorage.setItem('demoEquipment', JSON.stringify(demoEquipment));
  }, [demoEquipment]);
  useEffect(() => {
    localStorage.setItem('excavationLabor', JSON.stringify(excavationLabor));
  }, [excavationLabor]);
  useEffect(() => {
    localStorage.setItem('excavationEquipment', JSON.stringify(excavationEquipment));
  }, [excavationEquipment]);
  useEffect(() => {
    localStorage.setItem('backfillLabor', JSON.stringify(backfillLabor));
  }, [backfillLabor]);
  useEffect(() => {
    localStorage.setItem('backfillEquipment', JSON.stringify(backfillEquipment));
  }, [backfillEquipment]);
  useEffect(() => {
    localStorage.setItem('importLabor', JSON.stringify(importLabor));
  }, [importLabor]);
  useEffect(() => {
    localStorage.setItem('importEquipment', JSON.stringify(importEquipment));
  }, [importEquipment]);
  useEffect(() => {
    localStorage.setItem('civilLabor', JSON.stringify(civilLabor));
  }, [civilLabor]);
  useEffect(() => {
    localStorage.setItem('civilEquipment', JSON.stringify(civilEquipment));
  }, [civilEquipment]);
  useEffect(() => {
    localStorage.setItem('environmentalLabor', JSON.stringify(environmentalLabor));
  }, [environmentalLabor]);
  useEffect(() => {
    localStorage.setItem('environmentalEquipment', JSON.stringify(environmentalEquipment));
  }, [environmentalEquipment]);
  useEffect(() => {
    localStorage.setItem('mobilization', JSON.stringify(mobilization));
  }, [mobilization]);

  // Save task durations to localStorage
  useEffect(() => {
    localStorage.setItem('demoTaskDuration', JSON.stringify(demoTaskDuration));
  }, [demoTaskDuration]);
  useEffect(() => {
    localStorage.setItem('excavationTaskDuration', JSON.stringify(excavationTaskDuration));
  }, [excavationTaskDuration]);
  useEffect(() => {
    localStorage.setItem('backfillTaskDuration', JSON.stringify(backfillTaskDuration));
  }, [backfillTaskDuration]);
  useEffect(() => {
    localStorage.setItem('importTaskDuration', JSON.stringify(importTaskDuration));
  }, [importTaskDuration]);
  useEffect(() => {
    localStorage.setItem('civilTaskDuration', JSON.stringify(civilTaskDuration));
  }, [civilTaskDuration]);
  useEffect(() => {
    localStorage.setItem('environmentalTaskDuration', JSON.stringify(environmentalTaskDuration));
  }, [environmentalTaskDuration]);

  // Helper function to generate unique IDs
  const generateId = useCallback(() => {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Combine default materials with custom materials
  const allDemoMaterials = useMemo(() => [...materialDatabase, ...customDemoMaterials], [customDemoMaterials]);
  const allBulkMaterials = useMemo(() => [...bulkMaterialDatabase, ...customBulkMaterials], [customBulkMaterials]);
  const allExcavationMaterials = useMemo(() => [...excavationDatabase, ...customExcavationDB], [customExcavationDB]);

  // Area-based material functions
  const addMaterial = useCallback(() => {
    if (!selectedMaterial || !squareFeet) {
      toast.error('Please select a material and enter square feet');
      return;
    }

    const sqFt = parseFloat(squareFeet);
    if (sqFt <= 0) {
      toast.error('Square feet must be greater than 0');
      return;
    }

    const materialInfo = allDemoMaterials.find(m => m.name === selectedMaterial);
    if (!materialInfo) {
      toast.error('Invalid material selected');
      return;
    }

    // Use custom weight if provided, otherwise use default
    const effectiveWeight = parseFloat(customWeight) || materialInfo.weightPerSqFt;
    const costPerTon = parseFloat(demoCostPerTon) || 0;
    const totalWeight = sqFt * effectiveWeight;
    const totalTons = totalWeight / 2000;
    const totalCost = totalTons * costPerTon;

    const newMaterial = {
      id: generateId(),
      name: materialInfo.name,
      category: materialInfo.category,
      weightPerSqFt: effectiveWeight,
      originalWeight: materialInfo.weightPerSqFt,
      customWeight: customWeight ? parseFloat(customWeight) : null,
      squareFeet: sqFt,
      totalWeight: totalWeight,
      totalTons: totalTons,
      costPerTon: costPerTon,
      totalCost: totalCost,
      addedAt: new Date().toISOString()
    };

    setMaterials([...materials, newMaterial]);
    toast.success(`${materialInfo.name} added successfully!`);
    
    setSelectedMaterial('');
    setSquareFeet('');
    setDemoCostPerTon('');
    setCustomWeight('');
  }, [selectedMaterial, squareFeet, customWeight, demoCostPerTon, materials, generateId, allDemoMaterials]);

  const updateMaterial = (id, updatedData) => {
    setMaterials(materials.map(material => {
      if (material.id === id) {
        const totalWeight = updatedData.squareFeet * material.weightPerSqFt;
        return { ...material, ...updatedData, totalWeight };
      }
      return material;
    }));
    toast.success('Material updated');
  };

  const deleteMaterial = (id) => {
    const materialToDelete = materials.find(m => m.id === id);
    setMaterials(materials.filter(material => material.id !== id));
    toast.error(`${materialToDelete?.name || 'Material'} removed`);
  };

  const clearAllMaterials = () => {
    setMaterials([]);
    localStorage.removeItem('calculatorMaterials');
    toast.info('All area materials cleared');
  };

  // Bulk material functions
  const addBulkMaterial = () => {
    if (!selectedBulkMaterial || !cubicYards) {
      toast.error('Please select a material and enter cubic yards');
      return;
    }

    const cy = parseFloat(cubicYards);
    if (cy <= 0) {
      toast.error('Cubic yards must be greater than 0');
      return;
    }

    const materialInfo = allBulkMaterials.find(m => m.name === selectedBulkMaterial);
    if (!materialInfo) {
      toast.error('Invalid material selected');
      return;
    }

    // Use custom weight if provided, otherwise use default
    const effectiveWeight = parseFloat(customBulkWeight) || materialInfo.tonsPerCubicYard;
    const materialCostPerTon = parseFloat(bulkCostPerTon) || 0;
    const truckingCostPerTon = parseFloat(bulkTruckingCostPerTon) || 0;

    // Calculate totals (no compaction for import - compaction is in backfill tab)
    const totalTons = cy * effectiveWeight;
    const totalMaterialCost = totalTons * materialCostPerTon;
    const totalTruckingCost = totalTons * truckingCostPerTon;
    const totalCost = totalMaterialCost + totalTruckingCost;

    const newMaterial = {
      id: generateId(),
      name: materialInfo.name,
      category: materialInfo.category,
      tonsPerCubicYard: effectiveWeight,
      originalTonsPerCY: materialInfo.tonsPerCubicYard,
      customWeight: customBulkWeight ? parseFloat(customBulkWeight) : null,
      cubicYards: cy,
      totalTons: totalTons,
      totalPounds: totalTons * 2000,
      materialCostPerTon: materialCostPerTon,
      truckingCostPerTon: truckingCostPerTon,
      totalMaterialCost: totalMaterialCost,
      totalTruckingCost: totalTruckingCost,
      costPerTon: materialCostPerTon + truckingCostPerTon,
      totalCost: totalCost,
      addedAt: new Date().toISOString()
    };

    setBulkMaterials([...bulkMaterials, newMaterial]);
    toast.success(`${materialInfo.name} added successfully!`);
    
    setSelectedBulkMaterial('');
    setCubicYards('');
    setBulkCostPerTon('');
    setBulkTruckingCostPerTon('');
    setCustomBulkWeight('');
  };

  const updateBulkMaterial = (id, updatedData) => {
    setBulkMaterials(bulkMaterials.map(material => {
      if (material.id === id) {
        const totalTons = updatedData.cubicYards * material.tonsPerCubicYard;
        const totalPounds = totalTons * 2000;
        return { ...material, ...updatedData, totalTons, totalPounds };
      }
      return material;
    }));
    toast.success('Material updated');
  };

  const deleteBulkMaterial = (id) => {
    const materialToDelete = bulkMaterials.find(m => m.id === id);
    setBulkMaterials(bulkMaterials.filter(material => material.id !== id));
    toast.error(`${materialToDelete?.name || 'Material'} removed`);
  };

  const clearAllBulkMaterials = () => {
    setBulkMaterials([]);
    localStorage.removeItem('bulkCalculatorMaterials');
    toast.info('All bulk materials cleared');
  };

  // Clear All functions for each tab
  const clearDemoTab = () => {
    setMaterials([]);
    setPipingMaterials([]);
    setTankMaterials([]);
    setBuildings([]);
    setDemoLabor([]);
    setDemoEquipment([]);
    setDemoTaskDuration({ value: '', unit: 'days' });
    localStorage.removeItem('calculatorMaterials');
    localStorage.removeItem('pipingMaterials');
    localStorage.removeItem('tankMaterials');
    localStorage.removeItem('buildings');
    localStorage.removeItem('demoLabor');
    localStorage.removeItem('demoEquipment');
    localStorage.removeItem('demoTaskDuration');
    toast.success('Demo tab cleared');
  };

  const clearExcavationTab = () => {
    setExcavationMaterials([]);
    setCustomExcavationVolumes([]);
    setExcavationLabor([]);
    setExcavationEquipment([]);
    setExcavationTaskDuration({ value: '', unit: 'days' });
    localStorage.removeItem('excavationMaterials');
    localStorage.removeItem('customExcavationVolumes');
    localStorage.removeItem('excavationLabor');
    localStorage.removeItem('excavationEquipment');
    localStorage.removeItem('excavationTaskDuration');
    toast.success('Excavation tab cleared');
  };

  const clearImportTab = () => {
    setBulkMaterials([]);
    setCustomImportVolumes([]);
    localStorage.removeItem('bulkCalculatorMaterials');
    localStorage.removeItem('customImportVolumes');
    toast.success('Import tab cleared');
  };

  const clearBackfillTab = () => {
    setBackfillItems([]);
    setBackfillLabor([]);
    setBackfillEquipment([]);
    setBackfillTaskDuration({ value: '', unit: 'days' });
    localStorage.removeItem('backfillItems');
    localStorage.removeItem('backfillLabor');
    localStorage.removeItem('backfillEquipment');
    localStorage.removeItem('backfillTaskDuration');
    toast.success('Backfill tab cleared');
  };

  const clearCivilTab = () => {
    setCivilItems([]);
    setCivilLabor([]);
    setCivilEquipment([]);
    setCivilTaskDuration({ value: '', unit: 'days' });
    localStorage.removeItem('civilItems');
    localStorage.removeItem('civilLabor');
    localStorage.removeItem('civilEquipment');
    localStorage.removeItem('civilTaskDuration');
    toast.success('Civil tab cleared');
  };

  const clearEnvironmentalTab = () => {
    setEnvironmentalItems([]);
    setEnvironmentalLabor([]);
    setEnvironmentalEquipment([]);
    setEnvironmentalTaskDuration({ value: '', unit: 'days' });
    localStorage.removeItem('environmentalItems');
    localStorage.removeItem('environmentalLabor');
    localStorage.removeItem('environmentalEquipment');
    localStorage.removeItem('environmentalTaskDuration');
    toast.success('Environmental tab cleared');
  };

  // Calculate totals
  const materialTotals = materials.reduce((acc, material) => {
    if (!acc[material.name]) {
      acc[material.name] = {
        name: material.name,
        category: material.category,
        totalSquareFeet: 0,
        totalWeight: 0,
        count: 0
      };
    }
    acc[material.name].totalSquareFeet += material.squareFeet;
    acc[material.name].totalWeight += material.totalWeight;
    acc[material.name].count += 1;
    return acc;
  }, {});

  const bulkMaterialTotals = bulkMaterials.reduce((acc, material) => {
    if (!acc[material.name]) {
      acc[material.name] = {
        name: material.name,
        category: material.category,
        totalCubicYards: 0,
        totalTons: 0,
        count: 0
      };
    }
    acc[material.name].totalCubicYards += material.cubicYards;
    acc[material.name].totalTons += material.totalTons;
    acc[material.name].count += 1;
    return acc;
  }, {});

  const grandTotal = {
    totalSquareFeet: materials.reduce((sum, m) => sum + m.squareFeet, 0),
    totalWeight: materials.reduce((sum, m) => sum + m.totalWeight, 0),
    totalCost: materials.reduce((sum, m) => sum + (m.totalCost || 0), 0),
    itemCount: materials.length
  };

  // Calculate custom import volumes totals (needed before bulkGrandTotal)
  const customImportTotals = {
    totalCubicYards: customImportVolumes.reduce((sum, v) => sum + (v.cubicYards || 0), 0),
    totalTons: customImportVolumes.reduce((sum, v) => sum + (v.totalTons || 0), 0),
    totalWeight: customImportVolumes.reduce((sum, v) => sum + (v.totalPounds || 0), 0),
    totalCost: customImportVolumes.reduce((sum, v) => sum + (v.totalCost || 0), 0),
    itemCount: customImportVolumes.length
  };

  // Calculate custom excavation volumes totals (needed before excavationGrandTotal)
  const customExcavationTotals = {
    totalCubicYards: customExcavationVolumes.reduce((sum, v) => sum + (v.cubicYards || 0), 0),
    totalTons: customExcavationVolumes.reduce((sum, v) => sum + (v.totalTons || 0), 0),
    totalWeight: customExcavationVolumes.reduce((sum, v) => sum + (v.totalPounds || 0), 0),
    totalCost: customExcavationVolumes.reduce((sum, v) => sum + (v.totalCost || 0), 0),
    itemCount: customExcavationVolumes.length
  };

  const bulkGrandTotal = {
    totalCubicYards: bulkMaterials.reduce((sum, m) => sum + m.cubicYards, 0) + customImportTotals.totalCubicYards,
    totalTons: bulkMaterials.reduce((sum, m) => sum + m.totalTons, 0) + customImportTotals.totalTons,
    totalMaterialCost: bulkMaterials.reduce((sum, m) => sum + (m.totalMaterialCost || 0), 0),
    totalTruckingCost: bulkMaterials.reduce((sum, m) => sum + (m.totalTruckingCost || 0), 0),
    totalCost: bulkMaterials.reduce((sum, m) => sum + (m.totalCost || 0), 0) + customImportTotals.totalCost,
    itemCount: bulkMaterials.length + customImportTotals.itemCount
  };

  // Calculate backfill totals
  const backfillGrandTotal = {
    totalLooseCY: backfillItems.reduce((sum, m) => sum + (m.looseCubicYards || 0), 0),
    totalCompactedCY: backfillItems.reduce((sum, m) => sum + (m.compactedCubicYards || 0), 0),
    totalTons: backfillItems.reduce((sum, m) => sum + (m.totalTons || 0), 0),
    totalMaterialCost: backfillItems.reduce((sum, m) => sum + (m.totalMaterialCost || 0), 0),
    totalTruckingCost: backfillItems.reduce((sum, m) => sum + (m.totalTruckingCost || 0), 0),
    totalCost: backfillItems.reduce((sum, m) => sum + (m.totalCost || 0), 0),
    itemCount: backfillItems.length
  };

  const excavationGrandTotal = {
    totalBankCY: excavationMaterials.reduce((sum, m) => sum + m.bankCubicYards, 0),
    totalLooseCY: excavationMaterials.reduce((sum, m) => sum + m.looseCubicYards, 0),
    totalTons: excavationMaterials.reduce((sum, m) => sum + m.totalTons, 0) + customExcavationTotals.totalTons,
    totalCost: excavationMaterials.reduce((sum, m) => sum + ((m.totalTons || 0) * (m.costPerTon || 0)), 0) + customExcavationTotals.totalCost,
    itemCount: excavationMaterials.length + customExcavationTotals.itemCount
  };

  // Calculate piping totals
  const pipingGrandTotal = {
    totalLinearFeet: pipingMaterials.reduce((sum, m) => sum + m.linearFeet, 0),
    totalWeight: pipingMaterials.reduce((sum, m) => sum + m.totalWeight, 0),
    totalTons: pipingMaterials.reduce((sum, m) => sum + ((m.totalTons || m.totalWeight / 2000) || 0), 0),
    totalFluidGallons: pipingMaterials.reduce((sum, m) => sum + m.totalFluidGallons, 0),
    totalCost: pipingMaterials.reduce((sum, m) => sum + (m.totalCost || 0), 0),
    itemCount: pipingMaterials.length
  };

  // Calculate tanks totals
  const tanksGrandTotal = {
    totalQuantity: tankMaterials.reduce((sum, m) => sum + m.quantity, 0),
    totalWeight: tankMaterials.reduce((sum, m) => sum + m.totalWeight, 0),
    totalTons: tankMaterials.reduce((sum, m) => sum + ((m.totalTons || m.totalWeight / 2000) || 0), 0),
    totalCost: tankMaterials.reduce((sum, m) => sum + (m.totalCost || 0), 0),
    itemCount: tankMaterials.length
  };

  // Calculate buildings totals
  const buildingsGrandTotal = {
    totalWeight: buildings.reduce((sum, b) => sum + (b.totalPounds || 0), 0),
    totalTons: buildings.reduce((sum, b) => sum + (b.totalTons || 0), 0),
    totalCost: buildings.reduce((sum, b) => sum + (b.totalCost || 0), 0),
    itemCount: buildings.length,
    componentCount: buildings.reduce((sum, b) => sum + (b.components?.length || 0), 0)
  };

  // Custom totals moved above for proper dependency order

  // Update grandTotal to include piping, tanks, and buildings (add to Demo total)
  const combinedDemoTotal = {
    totalSquareFeet: grandTotal.totalSquareFeet,
    totalWeight: grandTotal.totalWeight + pipingGrandTotal.totalWeight + tanksGrandTotal.totalWeight + buildingsGrandTotal.totalWeight,
    totalCost: grandTotal.totalCost + pipingGrandTotal.totalCost + tanksGrandTotal.totalCost + buildingsGrandTotal.totalCost,
    itemCount: grandTotal.itemCount + pipingGrandTotal.itemCount + tanksGrandTotal.itemCount + buildingsGrandTotal.itemCount
  };

  const groupedMaterials = allDemoMaterials.reduce((acc, material) => {
    const category = material.category || 'Custom';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(material);
    return acc;
  }, {});

  const groupedBulkMaterials = allBulkMaterials.reduce((acc, material) => {
    const category = material.category || 'Custom';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(material);
    return acc;
  }, {});

  const selectedMaterialData = allDemoMaterials.find(m => m.name === selectedMaterial);
  const selectedBulkMaterialData = allBulkMaterials.find(m => m.name === selectedBulkMaterial);

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <Header 
        visitCount={visitCount} 
        onQuickSave={handleQuickSave}
        lastSaveTime={lastSaveTime}
      />
      
      <main className="container mx-auto px-4 py-6 max-w-7xl">
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-foreground">Mass Master Pro - Construction Estimator</h1>
          {projectInfo.name && (
            <p className="text-muted-foreground">Project: {projectInfo.name}</p>
          )}
        </div>

        <Tabs defaultValue="mobilization" className="w-full">
          <TooltipProvider delayDuration={300}>
            <TabsList className="w-full max-w-7xl mx-auto mb-6 p-1 sm:p-1.5 h-auto bg-muted/50 backdrop-blur overflow-x-auto">
              <div className="flex flex-nowrap md:grid md:grid-cols-12 gap-0.5 sm:gap-1 min-w-max md:min-w-0">
                {/* 0. Mobilization - NEW */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <TabsTrigger value="mobilization" className="flex flex-col sm:flex-row items-center justify-center gap-0.5 text-[10px] sm:text-xs px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap data-[state=active]:bg-purple-600 data-[state=active]:text-white">
                      <PackageOpen className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                      <span>Mob/Demob</span>
                    </TabsTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs"><p>{TAB_TOOLTIPS.mobilization}</p></TooltipContent>
                </Tooltip>
                {/* 1. Demolition */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <TabsTrigger value="area" className="flex flex-col sm:flex-row items-center justify-center gap-0.5 text-[10px] sm:text-xs px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                      <Construction className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                      <span>Demolition</span>
                    </TabsTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs"><p>{TAB_TOOLTIPS.demo}</p></TooltipContent>
                </Tooltip>
                {/* 2. Excavation */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <TabsTrigger value="excavation" className="flex flex-col sm:flex-row items-center justify-center gap-0.5 text-[10px] sm:text-xs px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                      <Shovel className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                      <span>Excavation</span>
                    </TabsTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs"><p>{TAB_TOOLTIPS.excavation}</p></TooltipContent>
                </Tooltip>
                {/* 3. T&D */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <TabsTrigger value="trucking" className="flex flex-col sm:flex-row items-center justify-center gap-0.5 text-[10px] sm:text-xs px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                      <TruckIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                      <span>T&D</span>
                    </TabsTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs"><p>{TAB_TOOLTIPS.trucking}</p></TooltipContent>
                </Tooltip>
                {/* 4. Import */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <TabsTrigger value="bulk" className="flex flex-col sm:flex-row items-center justify-center gap-0.5 text-[10px] sm:text-xs px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                      <TruckIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0 scale-x-[-1]" />
                      <span>Import</span>
                    </TabsTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs"><p>{TAB_TOOLTIPS.import}</p></TooltipContent>
                </Tooltip>
                {/* 5. Backfill */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <TabsTrigger value="backfill" className="flex flex-col sm:flex-row items-center justify-center gap-0.5 text-[10px] sm:text-xs px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap data-[state=active]:bg-teal-600 data-[state=active]:text-white">
                      <Layers className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                      <span>Backfill</span>
                    </TabsTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs"><p>{TAB_TOOLTIPS.backfill}</p></TooltipContent>
                </Tooltip>
                {/* 6. Civil */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <TabsTrigger value="civil" className="flex flex-col sm:flex-row items-center justify-center gap-0.5 text-[10px] sm:text-xs px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap data-[state=active]:bg-orange-500 data-[state=active]:text-white">
                      <HardHat className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                      <span>Civil</span>
                    </TabsTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs"><p>{TAB_TOOLTIPS.civil}</p></TooltipContent>
                </Tooltip>
                {/* 7. Environmental */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <TabsTrigger value="environmental" className="flex flex-col sm:flex-row items-center justify-center gap-0.5 text-[10px] sm:text-xs px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap data-[state=active]:bg-green-600 data-[state=active]:text-white">
                      <Leaf className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                      <span>Environmental</span>
                    </TabsTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs"><p>{TAB_TOOLTIPS.environmental}</p></TooltipContent>
                </Tooltip>
                {/* 8. Tools */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <TabsTrigger value="tools" className="flex flex-col sm:flex-row items-center justify-center gap-0.5 text-[10px] sm:text-xs px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap data-[state=active]:bg-accent data-[state=active]:text-accent-foreground">
                      <Wrench className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                      <span>Tools</span>
                    </TabsTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs"><p>{TAB_TOOLTIPS.tools}</p></TooltipContent>
                </Tooltip>
                {/* 9. Estimate */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <TabsTrigger value="estimate" className="flex flex-col sm:flex-row items-center justify-center gap-0.5 text-[10px] sm:text-xs px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap data-[state=active]:bg-success data-[state=active]:text-success-foreground">
                      <ClipboardList className="w-3 h-3 sm:w-4 sm:h-4 shrink-0" />
                      <span className="text-[8px] sm:text-xs leading-tight">Estimate</span>
                    </TabsTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs"><p>{TAB_TOOLTIPS.estimate}</p></TooltipContent>
                </Tooltip>
                {/* 9. RSMeans */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <TabsTrigger value="rsmeans" className="flex flex-col sm:flex-row items-center justify-center gap-1 text-xs px-1 sm:px-2 py-2 sm:py-3 data-[state=active]:bg-purple-600 data-[state=active]:text-white">
                      <Database className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                      <span>RSMeans</span>
                    </TabsTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs"><p>{TAB_TOOLTIPS.rsmeans}</p></TooltipContent>
                </Tooltip>
                {/* 11. Contractors */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <TabsTrigger value="contractors" className="flex flex-col sm:flex-row items-center justify-center gap-0.5 text-[10px] sm:text-xs px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap data-[state=active]:bg-indigo-600 data-[state=active]:text-white">
                      <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                      <span>Contacts</span>
                    </TabsTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs"><p>{TAB_TOOLTIPS.contractors}</p></TooltipContent>
                </Tooltip>

                {/* 12. AI Documents */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <TabsTrigger value="documents" className="flex flex-col sm:flex-row items-center justify-center gap-0.5 text-[10px] sm:text-xs px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-600 data-[state=active]:to-pink-600 data-[state=active]:text-white" data-testid="documents-tab">
                      <Brain className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                      <span>AI Docs</span>
                    </TabsTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs"><p>AI-powered document analysis - Upload specs, bids, contracts for automatic summarization and data extraction</p></TooltipContent>
                </Tooltip>
              </div>
            </TabsList>
          </TooltipProvider>

          {/* MOBILIZATION/DEMOBILIZATION CALCULATOR - NEW */}
          <TabsContent value="mobilization" className="space-y-8">
            {/* Tab Header with Clear All */}
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-foreground">Mobilization & Demobilization</h2>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={clearMobilizationTab}
                className="border-destructive/50 text-destructive hover:bg-destructive/10"
                data-testid="clear-mobilization-tab"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear All
              </Button>
            </div>
            
            <MobilizationCalculator
              mobilizationData={mobilizationData}
              setMobilizationData={setMobilizationData}
            />
          </TabsContent>

          {/* DEMOLITION CALCULATOR */}
          <TabsContent value="area" className="space-y-8">
            {/* Tab Header with Clear All */}
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-foreground">Demolition Calculator</h2>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={clearDemoTab}
                className="border-destructive/50 text-destructive hover:bg-destructive/10"
                data-testid="clear-demo-tab"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear All
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-card border border-border rounded-lg p-6 shadow-card transition-smooth hover:shadow-elegant">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-muted-foreground">Total Items</span>
                  <Construction className="w-5 h-5 text-primary" />
                </div>
                <p className="text-3xl font-bold text-foreground">{combinedDemoTotal.itemCount}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {grandTotal.itemCount} materials + {pipingGrandTotal.itemCount} piping + {tanksGrandTotal.itemCount} tanks + {buildingsGrandTotal.itemCount} buildings
                </p>
              </div>
              
              <div className="bg-card border border-border rounded-lg p-6 shadow-card transition-smooth hover:shadow-elegant">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-muted-foreground">Total Area</span>
                  <TrendingUp className="w-5 h-5 text-accent" />
                </div>
                <p className="text-3xl font-bold text-foreground">
                  {grandTotal.totalSquareFeet.toLocaleString()}
                  <span className="text-lg text-muted-foreground ml-2">sq ft</span>
                </p>
              </div>
              
              <div className="bg-card border border-border rounded-lg p-6 shadow-card transition-smooth hover:shadow-elegant">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-muted-foreground">Total Weight</span>
                  <Weight className="w-5 h-5 text-success" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-foreground">
                    {(combinedDemoTotal.totalWeight / 2000).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    <span className="text-lg text-muted-foreground ml-2">tons</span>
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {(grandTotal.totalWeight / 2000).toFixed(1)}t materials + {(pipingGrandTotal.totalWeight / 2000).toFixed(1)}t piping + {(tanksGrandTotal.totalWeight / 2000).toFixed(1)}t tanks + {buildingsGrandTotal.totalTons.toFixed(1)}t buildings
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-1">
                <Card className="border border-border shadow-card bg-card sticky top-4">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-xl flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Plus className="w-5 h-5 text-primary" />
                        Add Material
                      </div>
                      <CustomMaterialsManager
                        materials={materialDatabase}
                        onMaterialsChange={(newList) => setCustomDemoMaterials(newList.filter(m => m.isCustom))}
                        storageKey="demoMaterials"
                        materialType="demo"
                        weightLabel="lbs/sq ft"
                        weightField="weightPerSqFt"
                      />
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="material" className="text-sm font-medium text-foreground">
                        Material Type
                      </Label>
                      <Select value={selectedMaterial} onValueChange={(val) => {
                        setSelectedMaterial(val);
                        setCustomWeight(''); // Reset custom weight when material changes
                      }}>
                        <SelectTrigger id="material" className="w-full">
                          <SelectValue placeholder="Select a material" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[400px]">
                          {Object.entries(groupedMaterials).map(([category, mats]) => (
                            <div key={category}>
                              <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50">
                                {category}
                              </div>
                              {mats.map(material => (
                                <SelectItem key={material.name} value={material.name}>
                                  <div className="flex items-center justify-between w-full pr-4">
                                    <span className="text-sm">{material.name}</span>
                                    <span className="text-xs text-muted-foreground ml-2">
                                      {material.weightPerSqFt} lbs/ft²
                                    </span>
                                  </div>
                                </SelectItem>
                              ))}
                            </div>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {selectedMaterialData && (
                      <div className="bg-muted/50 rounded-lg p-3 space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Category:</span>
                          <span className="font-semibold text-foreground">{selectedMaterialData.category}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Default weight:</span>
                          <span className="font-semibold text-primary">{selectedMaterialData.weightPerSqFt} lbs/ft²</span>
                        </div>
                        {selectedMaterialData.description && (
                          <p className="text-xs text-muted-foreground mt-2 pt-2 border-t border-border">
                            {selectedMaterialData.description}
                          </p>
                        )}
                      </div>
                    )}

                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor="squareFeet" className="text-sm font-medium text-foreground">
                          Square Feet
                        </Label>
                        <Input
                          id="squareFeet"
                          type="number"
                          placeholder="Enter area"
                          value={squareFeet}
                          onChange={(e) => setSquareFeet(e.target.value)}
                          min="0"
                          step="0.01"
                          className="w-full"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="customWeight" className="text-sm font-medium text-foreground">
                          Custom lbs/ft² 
                        </Label>
                        <Input
                          id="customWeight"
                          type="number"
                          placeholder={selectedMaterialData?.weightPerSqFt?.toString() || "Override"}
                          value={customWeight}
                          onChange={(e) => setCustomWeight(e.target.value)}
                          min="0"
                          step="0.01"
                          className="w-full"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="demoCostPerTon" className="text-sm font-medium text-foreground">
                          $/Ton
                        </Label>
                        <Input
                          id="demoCostPerTon"
                          type="number"
                          placeholder="$0.00"
                          value={demoCostPerTon}
                          onChange={(e) => setDemoCostPerTon(e.target.value)}
                          min="0"
                          step="0.01"
                          className="w-full"
                        />
                      </div>
                    </div>

                    {selectedMaterialData && squareFeet > 0 && (
                      <div className="bg-primary/10 border border-primary/30 rounded-lg p-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <p className="text-sm text-muted-foreground mb-1">Estimated Weight:</p>
                            <p className="text-2xl font-bold text-primary">
                              {((parseFloat(squareFeet) * (parseFloat(customWeight) || selectedMaterialData.weightPerSqFt)) / 2000).toFixed(2)} tons
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {(parseFloat(squareFeet) * (parseFloat(customWeight) || selectedMaterialData.weightPerSqFt)).toLocaleString(undefined, { maximumFractionDigits: 2 })} lbs
                              {customWeight && <span className="text-primary ml-1">(custom weight)</span>}
                            </p>
                          </div>
                          {demoCostPerTon > 0 && (
                            <div>
                              <p className="text-sm text-muted-foreground mb-1">Estimated Cost:</p>
                              <p className="text-2xl font-bold text-success">
                                ${(((parseFloat(squareFeet) * (parseFloat(customWeight) || selectedMaterialData.weightPerSqFt)) / 2000) * parseFloat(demoCostPerTon)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                ${parseFloat(demoCostPerTon).toFixed(2)}/ton
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <Button
                      onClick={addMaterial}
                      className="w-full bg-primary hover:bg-primary/90 mt-4"
                      size="lg"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Material
                    </Button>
                  </CardContent>
                </Card>
              </div>

              <div className="lg:col-span-1 space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-semibold text-foreground">Materials List</h2>
                  {materials.length > 0 && (
                    <Button
                      variant="outline"
                      onClick={clearAllMaterials}
                      size="sm"
                      className="border-destructive/50 text-destructive hover:bg-destructive/10"
                    >
                      Clear All
                    </Button>
                  )}
                </div>
                
                {materials.length === 0 ? (
                  <div className="bg-card border border-dashed border-border rounded-lg p-12 text-center">
                    <Construction className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                    <h3 className="text-xl font-semibold text-foreground mb-2">No Materials Added</h3>
                    <p className="text-muted-foreground">
                      Select a material and add square footage to start calculating
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {materials.map(material => (
                      <MaterialCard
                        key={material.id}
                        material={material}
                        onUpdate={updateMaterial}
                        onDelete={deleteMaterial}
                      />
                    ))}
                  </div>
                )}
              </div>

              <div className="lg:col-span-1">
                <SummaryCard
                  materialTotals={Object.values(materialTotals)}
                  grandTotal={grandTotal}
                />
              </div>
            </div>
            
            {/* Oil Field Piping Section */}
            <div className="border-t border-border pt-8">
              <h2 className="text-2xl font-semibold text-foreground mb-6">Oil Field Piping</h2>
              <PipingCalculator 
                pipingMaterials={pipingMaterials}
                setPipingMaterials={setPipingMaterials}
              />
            </div>
            
            {/* Oil Field Tanks Section */}
            <div className="border-t border-border pt-8">
              <h2 className="text-2xl font-semibold text-foreground mb-6">Oil Field Storage Tanks</h2>
              <TanksCalculator 
                tankMaterials={tankMaterials}
                setTankMaterials={setTankMaterials}
              />
            </div>
            
            {/* Custom Building Calculator Section */}
            <div className="border-t border-border pt-8">
              <BuildingCalculator 
                buildings={buildings}
                setBuildings={setBuildings}
              />
            </div>
            
            {/* Demo Task Duration */}
            <div className="border-t border-border pt-8">
              <Card className="border-l-4 border-l-blue-500">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="flex items-center gap-2">
                      <Construction className="w-5 h-5 text-blue-500" />
                      <Label className="font-semibold">Demo Task Duration</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        placeholder="0"
                        value={demoTaskDuration.value}
                        onChange={(e) => setDemoTaskDuration({...demoTaskDuration, value: e.target.value})}
                        min="0"
                        step="0.5"
                        className="w-24"
                        data-testid="demo-task-duration"
                      />
                      <Select value={demoTaskDuration.unit} onValueChange={(val) => setDemoTaskDuration({...demoTaskDuration, unit: val})}>
                        <SelectTrigger className="w-28">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hours">Hours</SelectItem>
                          <SelectItem value="days">Days</SelectItem>
                          <SelectItem value="weeks">Weeks</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Demo Labor & Equipment Section */}
            <div className="border-t border-border pt-8">
              <h2 className="text-2xl font-semibold text-foreground mb-6">Demo Labor & Equipment</h2>
              <LaborEquipmentInput
                laborItems={demoLabor}
                setLaborItems={setDemoLabor}
                equipmentItems={demoEquipment}
                setEquipmentItems={setDemoEquipment}
                tabType="demo"
              />
            </div>
          </TabsContent>

          {/* IMPORT MATERIALS CALCULATOR */}
          <TabsContent value="bulk" className="space-y-8">
            {/* Tab Header with Clear All */}
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-foreground">Import Materials Calculator</h2>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={clearImportTab}
                className="border-destructive/50 text-destructive hover:bg-destructive/10"
                data-testid="clear-import-tab"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear All
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-card border border-border rounded-lg p-6 shadow-card transition-smooth hover:shadow-elegant">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-muted-foreground">Total Items</span>
                  <TruckIcon className="w-5 h-5 text-primary scale-x-[-1]" />
                </div>
                <p className="text-3xl font-bold text-foreground">{bulkGrandTotal.itemCount}</p>
              </div>
              
              <div className="bg-card border border-border rounded-lg p-6 shadow-card transition-smooth hover:shadow-elegant">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-muted-foreground">Total Volume</span>
                  <TrendingUp className="w-5 h-5 text-accent" />
                </div>
                <p className="text-3xl font-bold text-foreground">
                  {bulkGrandTotal.totalCubicYards.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  <span className="text-lg text-muted-foreground ml-2">cy</span>
                </p>
              </div>
              
              <div className="bg-card border border-border rounded-lg p-6 shadow-card transition-smooth hover:shadow-elegant">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-muted-foreground">Total Weight</span>
                  <Weight className="w-5 h-5 text-success" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-foreground">
                    {bulkGrandTotal.totalTons.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    <span className="text-lg text-muted-foreground ml-2">tons</span>
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {(bulkGrandTotal.totalTons * 2000).toLocaleString(undefined, { maximumFractionDigits: 0 })} lbs
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-1">
                <Card className="border border-border shadow-card bg-card sticky top-4">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-xl flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Plus className="w-5 h-5 text-primary" />
                        Add Import Material
                      </div>
                      <CustomMaterialsManager
                        materials={bulkMaterialDatabase}
                        onMaterialsChange={(newList) => setCustomBulkMaterials(newList.filter(m => m.isCustom))}
                        storageKey="bulkMaterials"
                        materialType="bulk"
                        weightLabel="tons/cy"
                        weightField="tonsPerCubicYard"
                      />
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="bulkMaterial" className="text-sm font-medium text-foreground">
                        Material Type
                      </Label>
                      <Select value={selectedBulkMaterial} onValueChange={(val) => {
                        setSelectedBulkMaterial(val);
                        setCustomBulkWeight('');
                      }}>
                        <SelectTrigger id="bulkMaterial" className="w-full">
                          <SelectValue placeholder="Select a material" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[400px]">
                          {Object.entries(groupedBulkMaterials).map(([category, mats]) => (
                            <div key={category}>
                              <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50">
                                {category}
                              </div>
                              {mats.map(material => (
                                <SelectItem key={material.name} value={material.name}>
                                  <div className="flex items-center justify-between w-full pr-4">
                                    <span className="text-sm">{material.name}</span>
                                    <span className="text-xs text-muted-foreground ml-2">
                                      {material.tonsPerCubicYard} tons/cy
                                    </span>
                                  </div>
                                </SelectItem>
                              ))}
                            </div>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {selectedBulkMaterialData && (
                      <div className="bg-muted/50 rounded-lg p-3 space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Category:</span>
                          <span className="font-semibold text-foreground">{selectedBulkMaterialData.category}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Default weight:</span>
                          <span className="font-semibold text-success">{selectedBulkMaterialData.tonsPerCubicYard} tons/cy</span>
                        </div>
                        {selectedBulkMaterialData.description && (
                          <p className="text-xs text-muted-foreground mt-2 pt-2 border-t border-border">
                            {selectedBulkMaterialData.description}
                          </p>
                        )}
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor="cubicYards" className="text-sm font-medium text-foreground">
                          Cubic Yards
                        </Label>
                        <Input
                          id="cubicYards"
                          type="number"
                          placeholder="Enter volume"
                          value={cubicYards}
                          onChange={(e) => setCubicYards(e.target.value)}
                          min="0"
                          step="0.01"
                          className="w-full"
                          data-testid="import-cy-input"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="customBulkWeight" className="text-sm font-medium text-foreground">
                          Custom tons/cy
                        </Label>
                        <Input
                          id="customBulkWeight"
                          type="number"
                          placeholder={selectedBulkMaterialData?.tonsPerCubicYard?.toString() || "Override"}
                          value={customBulkWeight}
                          onChange={(e) => setCustomBulkWeight(e.target.value)}
                          min="0"
                          step="0.01"
                          className="w-full"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor="bulkCostPerTon" className="text-sm font-medium text-foreground">
                          Material $/Ton
                        </Label>
                        <Input
                          id="bulkCostPerTon"
                          type="number"
                          placeholder="$0.00"
                          value={bulkCostPerTon}
                          onChange={(e) => setBulkCostPerTon(e.target.value)}
                          min="0"
                          step="0.01"
                          className="w-full"
                          data-testid="import-material-cost"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="bulkTruckingCostPerTon" className="text-sm font-medium text-foreground">
                          Trucking $/Ton
                        </Label>
                        <Input
                          id="bulkTruckingCostPerTon"
                          type="number"
                          placeholder="$0.00"
                          value={bulkTruckingCostPerTon}
                          onChange={(e) => setBulkTruckingCostPerTon(e.target.value)}
                          min="0"
                          step="0.01"
                          className="w-full"
                          data-testid="import-trucking-cost"
                        />
                      </div>
                    </div>

                    {selectedBulkMaterialData && cubicYards > 0 && (
                      <div className="bg-success/10 border border-success/30 rounded-lg p-3 space-y-2">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <p className="text-sm text-muted-foreground mb-1">Volume:</p>
                            <p className="text-lg font-bold text-accent">
                              {parseFloat(cubicYards).toLocaleString(undefined, { maximumFractionDigits: 2 })} cy
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground mb-1">Estimated Weight:</p>
                            <p className="text-lg font-bold text-success">
                              {(parseFloat(cubicYards) * (parseFloat(customBulkWeight) || selectedBulkMaterialData.tonsPerCubicYard)).toLocaleString(undefined, { maximumFractionDigits: 2 })} tons
                            </p>
                            {customBulkWeight && <p className="text-xs text-primary">(custom weight)</p>}
                          </div>
                        </div>
                        {(bulkCostPerTon > 0 || bulkTruckingCostPerTon > 0) && (
                          <div className="pt-2 border-t border-success/30 mt-2">
                            <div className="grid grid-cols-2 gap-2 text-sm mb-2">
                              <div>
                                <span className="text-muted-foreground">Material:</span>
                                <span className="font-semibold text-foreground ml-2">
                                  ${(parseFloat(cubicYards) * (parseFloat(customBulkWeight) || selectedBulkMaterialData.tonsPerCubicYard) * parseFloat(bulkCostPerTon || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Trucking:</span>
                                <span className="font-semibold text-foreground ml-2">
                                  ${(parseFloat(cubicYards) * (parseFloat(customBulkWeight) || selectedBulkMaterialData.tonsPerCubicYard) * parseFloat(bulkTruckingCostPerTon || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                              </div>
                            </div>
                            <p className="text-xl font-bold text-success">
                              Total: ${(parseFloat(cubicYards) * (parseFloat(customBulkWeight) || selectedBulkMaterialData.tonsPerCubicYard) * (parseFloat(bulkCostPerTon || 0) + parseFloat(bulkTruckingCostPerTon || 0))).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    <Button
                      onClick={addBulkMaterial}
                      className="w-full bg-primary hover:bg-primary/90 mt-4"
                      size="lg"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Material
                    </Button>
                  </CardContent>
                </Card>
              </div>

              <div className="lg:col-span-1 space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-semibold text-foreground">Import Materials List</h2>
                  {bulkMaterials.length > 0 && (
                    <Button
                      variant="outline"
                      onClick={clearAllBulkMaterials}
                      size="sm"
                      className="border-destructive/50 text-destructive hover:bg-destructive/10"
                    >
                      Clear All
                    </Button>
                  )}
                </div>
                
                {bulkMaterials.length === 0 ? (
                  <div className="bg-card border border-dashed border-border rounded-lg p-12 text-center">
                    <TruckIcon className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50 scale-x-[-1]" />
                    <h3 className="text-xl font-semibold text-foreground mb-2">No Materials Added</h3>
                    <p className="text-muted-foreground">
                      Select a bulk material and add cubic yards to start calculating
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {bulkMaterials.map(material => (
                      <BulkMaterialCard
                        key={material.id}
                        material={material}
                        onUpdate={updateBulkMaterial}
                        onDelete={deleteBulkMaterial}
                      />
                    ))}
                  </div>
                )}
              </div>

              <div className="lg:col-span-1">
                <BulkSummaryCard
                  materialTotals={Object.values(bulkMaterialTotals)}
                  grandTotal={bulkGrandTotal}
                />
              </div>
            </div>
            
            {/* Custom Volume Calculator */}
            <div className="border-t border-border pt-8">
              <CustomVolumeCalculator 
                volumes={customImportVolumes}
                setVolumes={setCustomImportVolumes}
                materials={customImportMaterialsList}
                title="Custom Import Volume Calculator (L × W × H)"
                factorLabel="Weight Factor"
                factorDescription="Override default tons/cy for materials"
                storageKey="customImportMaterials"
              />
            </div>
          </TabsContent>

          {/* BACKFILL CALCULATOR */}
          <TabsContent value="backfill" className="space-y-8">
            {/* Tab Header with Clear All */}
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-foreground">Backfill Calculator</h2>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={clearBackfillTab}
                className="border-destructive/50 text-destructive hover:bg-destructive/10"
                data-testid="clear-backfill-tab"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear All
              </Button>
            </div>
            
            <BackfillCalculator 
              backfillItems={backfillItems}
              setBackfillItems={setBackfillItems}
              taskDuration={backfillTaskDuration}
              setTaskDuration={setBackfillTaskDuration}
            />
            
            {/* Backfill Labor & Equipment Section */}
            <div className="border-t border-border pt-8">
              <h2 className="text-2xl font-semibold text-foreground mb-6">Backfill Labor & Equipment</h2>
              <LaborEquipmentInput
                laborItems={backfillLabor}
                setLaborItems={setBackfillLabor}
                equipmentItems={backfillEquipment}
                setEquipmentItems={setBackfillEquipment}
                tabType="backfill"
              />
            </div>
          </TabsContent>

          {/* EXCAVATION CALCULATOR */}
          <TabsContent value="excavation" className="space-y-8">
            {/* Tab Header with Clear All */}
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-foreground">Excavation Calculator</h2>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={clearExcavationTab}
                className="border-destructive/50 text-destructive hover:bg-destructive/10"
                data-testid="clear-excavation-tab"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear All
              </Button>
            </div>
            
            <ExcavationCalculator 
              excavationMaterials={excavationMaterials}
              setExcavationMaterials={setExcavationMaterials}
            />
            
            {/* Custom Volume Calculator for Excavation */}
            <div className="border-t border-border pt-8">
              <CustomVolumeCalculator 
                volumes={customExcavationVolumes}
                setVolumes={setCustomExcavationVolumes}
                materials={customExcavationMaterialsList}
                title="Custom Excavation Volume Calculator (L × W × H)"
                factorLabel="Swell Factor"
                factorDescription="1.0 = Bank measure, 1.25 = 25% swell for loose material"
                storageKey="customExcavationMaterials"
              />
            </div>
            
            {/* Excavation Task Duration */}
            <div className="border-t border-border pt-8">
              <Card className="border-l-4 border-l-blue-500">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="flex items-center gap-2">
                      <Shovel className="w-5 h-5 text-blue-500" />
                      <Label className="font-semibold">Excavation Task Duration</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        placeholder="0"
                        value={excavationTaskDuration.value}
                        onChange={(e) => setExcavationTaskDuration({...excavationTaskDuration, value: e.target.value})}
                        min="0"
                        step="0.5"
                        className="w-24"
                        data-testid="excavation-task-duration"
                      />
                      <Select value={excavationTaskDuration.unit} onValueChange={(val) => setExcavationTaskDuration({...excavationTaskDuration, unit: val})}>
                        <SelectTrigger className="w-28">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hours">Hours</SelectItem>
                          <SelectItem value="days">Days</SelectItem>
                          <SelectItem value="weeks">Weeks</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Excavation Labor & Equipment Section */}
            <div className="border-t border-border pt-8">
              <h2 className="text-2xl font-semibold text-foreground mb-6">Excavation Labor & Equipment</h2>
              <LaborEquipmentInput
                laborItems={excavationLabor}
                setLaborItems={setExcavationLabor}
                equipmentItems={excavationEquipment}
                setEquipmentItems={setExcavationEquipment}
                tabType="excavation"
              />
            </div>
          </TabsContent>

          {/* TRUCKING CALCULATOR */}
          <TabsContent value="trucking">
            <TruckingCalculator 
              areaTotals={grandTotal}
              pipingTotals={pipingGrandTotal}
              tanksTotals={tanksGrandTotal}
              buildingsTotals={buildingsGrandTotal}
              bulkTotals={bulkGrandTotal}
              excavationTotals={excavationGrandTotal}
              customImportTotals={customImportTotals}
              customExcavationTotals={customExcavationTotals}
              truckingCalculations={truckingCalculations}
              setTruckingCalculations={setTruckingCalculations}
            />
          </TabsContent>

          {/* CIVIL CONSTRUCTION CALCULATOR */}
          <TabsContent value="civil" className="space-y-8">
            {/* Tab Header with Clear All */}
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-foreground">Civil Construction Calculator</h2>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={clearCivilTab}
                className="border-destructive/50 text-destructive hover:bg-destructive/10"
                data-testid="clear-civil-tab"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear All
              </Button>
            </div>
            
            <CivilCalculator
              civilItems={civilItems}
              setCivilItems={setCivilItems}
            />
            
            {/* Civil Task Duration */}
            <div className="border-t border-border pt-8">
              <Card className="border-l-4 border-l-blue-500">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="flex items-center gap-2">
                      <HardHat className="w-5 h-5 text-blue-500" />
                      <Label className="font-semibold">Civil Task Duration</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        placeholder="0"
                        value={civilTaskDuration.value}
                        onChange={(e) => setCivilTaskDuration({...civilTaskDuration, value: e.target.value})}
                        min="0"
                        step="0.5"
                        className="w-24"
                        data-testid="civil-task-duration"
                      />
                      <Select value={civilTaskDuration.unit} onValueChange={(val) => setCivilTaskDuration({...civilTaskDuration, unit: val})}>
                        <SelectTrigger className="w-28">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hours">Hours</SelectItem>
                          <SelectItem value="days">Days</SelectItem>
                          <SelectItem value="weeks">Weeks</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Civil Labor & Equipment Section */}
            <div className="border-t border-border pt-8">
              <h2 className="text-2xl font-semibold text-foreground mb-6">Civil Labor & Equipment</h2>
              <LaborEquipmentInput
                laborItems={civilLabor}
                setLaborItems={setCivilLabor}
                equipmentItems={civilEquipment}
                setEquipmentItems={setCivilEquipment}
                tabType="civil"
              />
            </div>
          </TabsContent>

          {/* ENVIRONMENTAL CALCULATOR */}
          <TabsContent value="environmental" className="space-y-8">
            {/* Tab Header with Clear All */}
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-foreground">Environmental Calculator</h2>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={clearEnvironmentalTab}
                className="border-destructive/50 text-destructive hover:bg-destructive/10"
                data-testid="clear-environmental-tab"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear All
              </Button>
            </div>
            
            <EnvironmentalCalculator
              environmentalItems={environmentalItems}
              setEnvironmentalItems={setEnvironmentalItems}
            />
            
            {/* Environmental Task Duration */}
            <div className="border-t border-border pt-8">
              <Card className="border-l-4 border-l-blue-500">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="flex items-center gap-2">
                      <Leaf className="w-5 h-5 text-blue-500" />
                      <Label className="font-semibold">Environmental Task Duration</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        placeholder="0"
                        value={environmentalTaskDuration.value}
                        onChange={(e) => setEnvironmentalTaskDuration({...environmentalTaskDuration, value: e.target.value})}
                        min="0"
                        step="0.5"
                        className="w-24"
                        data-testid="environmental-task-duration"
                      />
                      <Select value={environmentalTaskDuration.unit} onValueChange={(val) => setEnvironmentalTaskDuration({...environmentalTaskDuration, unit: val})}>
                        <SelectTrigger className="w-28">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hours">Hours</SelectItem>
                          <SelectItem value="days">Days</SelectItem>
                          <SelectItem value="weeks">Weeks</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Environmental Labor & Equipment Section */}
            <div className="border-t border-border pt-8">
              <h2 className="text-2xl font-semibold text-foreground mb-6">Environmental Labor & Equipment</h2>
              <LaborEquipmentInput
                laborItems={environmentalLabor}
                setLaborItems={setEnvironmentalLabor}
                equipmentItems={environmentalEquipment}
                setEquipmentItems={setEnvironmentalEquipment}
                tabType="environmental"
              />
            </div>
          </TabsContent>

          {/* REPORTS TAB */}
          {/* QUICK CALCULATORS TAB */}
          <TabsContent value="tools">
            <div className="space-y-6">
              <AdvancedCalculators />
              <QuickCalculators />
              <EngineeringTools />
            </div>
          </TabsContent>

          {/* PROJECT ESTIMATE TAB - Excel-like Spreadsheet Interface */}
          <TabsContent value="estimate">
            <EstimateSpreadsheet 
              areaMaterials={materials}
              bulkMaterials={bulkMaterials}
              excavationMaterials={excavationMaterials}
              pipingMaterials={pipingMaterials}
              tankMaterials={tankMaterials}
              buildings={buildings}
              backfillItems={backfillItems}
              truckingCalculations={truckingCalculations}
              customImportVolumes={customImportVolumes}
              customExcavationVolumes={customExcavationVolumes}
              civilItems={civilItems}
              environmentalItems={environmentalItems}
              mobilizationData={mobilizationData}
              areaTotals={combinedDemoTotal}
              bulkTotals={bulkGrandTotal}
              excavationTotals={excavationGrandTotal}
              backfillTotals={backfillGrandTotal}
              pipingTotals={pipingGrandTotal}
              tanksTotals={tanksGrandTotal}
              buildingsTotals={buildingsGrandTotal}
              customImportTotals={customImportTotals}
              customExcavationTotals={customExcavationTotals}
              // Labor & Equipment from all calculators
              calculatorLaborEquipment={{
                demo: { labor: demoLabor, equipment: demoEquipment },
                excavation: { labor: excavationLabor, equipment: excavationEquipment },
                backfill: { labor: backfillLabor, equipment: backfillEquipment },
                civil: { labor: civilLabor, equipment: civilEquipment },
                environmental: { labor: environmentalLabor, equipment: environmentalEquipment }
              }}
              // Task Durations
              taskDurations={{
                demo: demoTaskDuration,
                excavation: excavationTaskDuration,
                backfill: backfillTaskDuration,
                civil: civilTaskDuration,
                environmental: environmentalTaskDuration
              }}
              mobilization={mobilization}
              setMobilization={setMobilization}
            />
          </TabsContent>

          {/* RSMeans Database */}
          <TabsContent value="rsmeans">
            <RSMeansDatabase />
          </TabsContent>

          {/* Contractor Database */}
          <TabsContent value="contractors">
            <ContractorDatabase />
          </TabsContent>

          {/* AI Document Analyzer */}
          <TabsContent value="documents">
            <DocumentAnalyzer onApplyToEstimate={(data) => {
              // Handle applying extracted data to estimate
              toast.success(`Extracted data ready to apply: ${data.materials?.length || 0} materials, ${data.labor_items?.length || 0} labor items`);
              // Navigate to estimate tab
              // The data could be passed to EstimateSpreadsheet via state or context
            }} />
          </TabsContent>
        </Tabs>
      </main>
      
      {/* AI Troubleshooter - Always available */}
      <AITroubleshooter />
    </div>
  );
};

export default CalculatorPage;